import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Clock, XCircle, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import API from '../../../util/API';
import ADMIN_PATHS from '../ADMIN_PATHS';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const StatusPill = ({ eligibility }) => {
  const canPay = Boolean(eligibility?.canPay);
  const cls = canPay
    ? 'border-amber-200 bg-amber-50 text-amber-800'
    : 'border-rose-200 bg-rose-50 text-rose-700';
  const label = canPay ? 'Awaiting payment' : 'Payment window expired';

  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-black', cls)}>
      <Clock className="w-3 h-3" />
      {label}
    </span>
  );
};

const FailedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [pageStatus, setPageStatus] = useState('idle'); // idle | loading | failed
  const [loadError, setLoadError] = useState('');
  const [cancelingId, setCancelingId] = useState('');

  const loadOrders = useCallback(async () => {
    setPageStatus('loading');
    setLoadError('');
    try {
      const res = await API.get('/order/failed');
      setOrders(Array.isArray(res?.data?.data) ? res.data.data : []);
      setPageStatus('succeeded');
    } catch (err) {
      setOrders([]);
      setPageStatus('failed');
      setLoadError(
        err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Failed to load unpaid orders'
      );
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleCancel = async (order) => {
    const orderKey = String(order?._id || order?.orderId || '').trim();
    if (!orderKey) return;

    const reason = window.prompt(
      `Cancel order ${order?.orderId || orderKey}? A cancellation reason is required.`,
      ''
    );
    if (reason == null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      window.alert('A cancellation reason is required.');
      return;
    }
    const confirmed = window.confirm(
      `Cancel order ${order?.orderId || orderKey} with reason "${trimmed}"? This cannot be undone from this page.`
    );
    if (!confirmed) return;

    setCancelingId(orderKey);
    try {
      const res = await API.post(`/order/${orderKey}/cancel`, { reason: trimmed });
      const message = res?.data?.message || 'Order cancelled.';
      if (res?.data?.wasPaid) {
        toast.warn(message);
      } else {
        toast.success(message);
      }
      await loadOrders();
    } catch (err) {
      const message =
        err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Cancel failed.';
      toast.error(message);
      await loadOrders();
    } finally {
      setCancelingId('');
    }
  };

  const rows = useMemo(() => {
    return (Array.isArray(orders) ? orders : []).map((o) => ({
      key: String(o?._id || o?.orderId || ''),
      orderId: String(o?.orderId || o?._id || '—'),
      linkId: String(o?._id || o?.orderId || ''),
      customer:
        o?.user?.name
        || [o?.user?.firstName, o?.user?.lastName].filter(Boolean).join(' ')
        || o?.user?.username
        || o?.user?.email
        || o?.shippingAddress?.fullName
        || '—',
      email: o?.user?.email || '',
      createdAt: o?.createdAt,
      totalPrice: Number(o?.totalPrice || 0),
      currency: String(o?.currency || 'USD'),
      paymentEligibility: o?.paymentEligibility || null,
    }));
  }, [orders]);

  return (
    <div className="p-6 space-y-6">
      <SectionHead
        title="Unpaid orders (payment queue)"
        subtitle="Real orders created at checkout that are still unpaid (order_status payment_unpaid). Customers can pay until the end of their 24-hour window."
        right={
          <button
            type="button"
            onClick={loadOrders}
            disabled={pageStatus === 'loading'}
            className={cx(
              'inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-primary',
              pageStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            <RefreshCw className={cx('w-4 h-4', pageStatus === 'loading' ? 'animate-spin' : '')} />
            Refresh
          </button>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-600">
        Data source: GET /order/failed (unpaid checkout drafts only). Retry/restore actions were
        removed: there is no backend endpoint that can pay or revive an order — the customer pays
        through checkout while their window is open, and admins can cancel from here.
      </div>

      {pageStatus === 'failed' ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {loadError}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Payment window</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Deadline</th>
                <th className="px-4 py-3 text-right text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageStatus === 'loading' ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                    Loading unpaid orders…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                    {pageStatus === 'failed' ? 'Could not load orders.' : 'No unpaid orders.'}
                  </td>
                </tr>
              ) : (
                rows.map((order) => (
                  <tr key={order.key || order.orderId} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm font-black text-primary">{order.orderId}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{order.customer}</p>
                      {order.email ? <p className="text-xs text-slate-500">{order.email}</p> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(order.createdAt)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-900">
                      {order.currency} {order.totalPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill eligibility={order.paymentEligibility} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {order.paymentEligibility?.expiresAt ? formatDateTime(order.paymentEligibility.expiresAt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${order.linkId}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleCancel({ _id: order.linkId, orderId: order.orderId })}
                          disabled={cancelingId === order.linkId}
                          className={cx(
                            'inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-black text-rose-700 hover:bg-rose-50',
                            cancelingId === order.linkId ? 'opacity-60 cursor-not-allowed' : ''
                          )}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          {cancelingId === order.linkId ? 'Cancelling…' : 'Cancel order'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default FailedOrders;
